//import { isPlatformBrowser } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { AccountService } from 'src/app/modules/account/account.service';
import { GoogleAuthService } from 'src/app/shared/services/google-auth.service';
import { SignOutService } from 'src/app/shared/services/sign-out.service';
import { UserService } from 'src/app/shared/services/user.service';
import { CultureService } from 'src/app/shared/culture/culture.service';

@Component({
    selector: 'app-account-menu',
    templateUrl: './account-menu.component.html',
    styleUrls: ['./account-menu.component.scss']
})
export class AccountMenuComponent 
{
    @Output() closeMenu: EventEmitter<void> = new EventEmitter<void>();
    
    loginFormMenu:FormGroup = this.formBuilder.group({
        email: ['', [Validators.required]],
        password: ['', [Validators.required]]
    });
    /// Signed in, the page about to reload: the menu shows that instead of the signed-in view for a moment.
    signingIn = false;
    @ViewChild('emailBox') emailBox: ElementRef<HTMLInputElement>;
    @ViewChild('passwordBox') passwordBox: ElementRef<HTMLInputElement>;

    /// The menu opened (the header's indicator): the cursor goes where typing starts - the email, or the password when the
    /// email is already there (typed before, or filled in by the browser). Without it a click on a field the browser had filled
    /// showed no cursor (the owner, 2026-11-02).
    opened(open: boolean): void
    {
        if (!open || this.isLoggedIn || !isPlatformBrowser(this.platformId)) { return; }
        // After the dropdown's opening transition, when it can take focus.
        setTimeout(() =>
        {
            const email = this.emailBox && this.emailBox.nativeElement;
            const password = this.passwordBox && this.passwordBox.nativeElement;
            const target = email && email.value ? password : email;
            if (target && typeof target.focus === 'function') { target.focus({ preventScroll: true } as any); }
        }, 180);
    }

    get initials(): string
    {
        const i = this.userInfo || {};
        const text = ((i.FirstName || '').trim().charAt(0) + (i.LastName || '').trim().charAt(0)) || (i.Email || '?').charAt(0);
        return text.toUpperCase();
    }

    get fullName(): string
    {
        const i = this.userInfo || {};
        return [i.FirstName, i.LastName].filter(x => !!x).join(' ') || i.Email || '';
    }
    userInfo:any = {};
    isLoggedIn:boolean;
    isOwner:boolean;
    defaultRoute:string = 'myaccount';
    isRequesting:boolean;
    requestingGoogle:boolean;
    constructor(private formBuilder: FormBuilder,
        @Inject(PLATFORM_ID) private platformId: any,
        private toastr:ToastrService,
        private accountService:AccountService,
        private signOutService:SignOutService,
        private userService:UserService,
        private googleAuth:GoogleAuthService,
        private culture:CultureService)
    {
        this.userService.onUserInfoChanged$.subscribe(res =>
        { 
            this.isLoggedIn = this.userService.isLoggedIn();
            this.userInfo = res;
            // Owner-only shortcuts; the pages behind them are gated server-side regardless.
            this.isOwner = this.userService.isNijera() === true;
            // The dashboard is the owner's figures; a seller has their own; everyone else lands on their account.
            const info = res || {};
            // A seller closed for good (a shopper again, AccountType 1, the seller id kept) lands on their account page.
            this.defaultRoute = this.isOwner ? 'dashboard'
                : info.SID > 0 && info.SID !== 786 && info.AccountType !== this.userService.userTypeConst.shopper ? 'shop/dashboard' : 'myaccount';
            // A seller (or their staff): their console's links instead of a shopper's.
            this.isSeller = !this.isOwner && this.defaultRoute === 'shop/dashboard';
            this.productsUrl = '/account/userproducts/' + String(info.Id || '').replace(/-/g, '');
        });
    }

    isSeller = false;
    productsUrl = '/account/userproducts';

    /**
     * Google sign-in from the header dropdown. No country is passed: this form has no
     * country field, so GoogleAuthService falls back to the one the site already
     * detected for this visitor.
     */
    continueWithGoogle(): void
    {
        if(this.requestingGoogle){ return; }

        this.requestingGoogle = true;

        this.googleAuth.signIn().then(finished =>
        {
            // false means the browser is navigating to Google - leave the button alone
            // rather than flickering it back as the page is replaced.
            if(finished)
            {
                this.requestingGoogle = false;
                this.closeMenu.emit();
            }
        });
    }

    onSubmit(event:any): void
    {
        event.preventDefault();  

        if(this.loginFormMenu.invalid){
            return;
        }
        
        this.isRequesting = true;
        var model:any = {};
        model.Email = this.loginFormMenu.value.email;
        model.Password = this.loginFormMenu.value.password;
        var em = this.loginFormMenu.value.email;

        this.accountService.login(model).subscribe(res => 
        { 
            // 0 and 1 both mean bad credentials. The server no longer distinguishes
            // "no such account" from "wrong password" - 0 is only still handled here
            // for older cached bundles.
            if(res === 0 || res === 1)
            {
                this.toastr.error(this.culture.t('sign-in-invalid', 'Invalid email or password.'), this.culture.t('sign-in-invalid-title', 'Invalid'), {timeOut:4000, closeButton:true});
                this.isRequesting = false;
            }
            else if(res === 2)
            {
                this.toastr.error(this.culture.t('sign-in-deactivated', 'Your account has been deactivated, please contact support.'), this.culture.t('sign-in-deactivated-title', 'Deactive'), {timeOut:4000, closeButton:true});
                this.isRequesting = false;
            }
            else if(res === 3)
            {
                this.toastr.error(this.culture.t('sign-in-unconfirmed', 'Your email is not confirmed, use forgot password to get a new email confirmation link.'), this.culture.t('sign-in-unconfirmed-title', 'Email Not Verified'), {timeOut:4000, closeButton:true});
                this.isRequesting = false;
            }
            else if(res === null || res === undefined)
            {
                this.toastr.error(this.culture.t('something-went-wrong', 'Something went wrong, try again!'), this.culture.t('error-title', 'Error'), {timeOut:4000, closeButton:true});
                this.isRequesting = false;
            }
            else
            {
                // From here the page reloads: the menu says "Signing you in" and stays open, rather than flipping to the
                // signed-in view and closing a moment before the page goes.
                this.signingIn = true;
                this.userService.setToken(res);

                this.userService.getAndInitUserInfo(em).subscribe(
                {
                    complete:()=>
                    {
                        if (isPlatformBrowser(this.platformId)){
                            window.location.reload();
                        }
                    },
                    error:()=>
                    {
                        // The token is kept; the reload reads the account afresh.
                        if (isPlatformBrowser(this.platformId)){
                            window.location.reload();
                        }
                    }
                });
            }
        }, error=>
        {
            // The server sends a short reference on a real failure, and writes the
            // same one to its log. Show it so a support report can be traced.
            const reference = error?.error?.reference;
            this.toastr.error(reference
                ? this.culture.t('something-went-wrong-reference', 'Something went wrong. Reference: {reference}', { reference })
                : this.culture.t('something-went-wrong', 'Something went wrong, try again!'), this.culture.t('error-title', 'Error'), {timeOut:4000, closeButton:true});
            this.isRequesting = false;
        });
    }

    logout()
    {
        this.closeMenu.emit();
        this.signOutService.signOut();
    }
}
